import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { FunctionComponent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  Popover,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
} from '@mui/material';
import { useMetaDataLogs } from '@/queries/users';
import { useMutation } from '@tanstack/react-query';
import { Logs } from '@/core/entities';
import { LogsResponse } from '@/api/users/types';
import ExportButton from '@/features/dashboard/ExportButton';
import SelectInput from '@/components/ui/select-input';
import {
  convertDateFilter,
  DATE_FILTER_OPTIONS,
  getStatisticsExportFileName,
} from '@/features/dashboard/utils';
import { DateFilter, QueryDateRange } from '@/features/dashboard/types';
import DateRangePicker from '@/components/ui/date-range-picker';
import { CustomDateRange } from '@/shared/types/date';
import LocationFiltersDialog, {
  SelectedLocation,
} from '@/features/dashboard/widgets/completed-cycles/filters/LocationFiltersDialog';
import { useCustomerSites } from '@/queries/customers';
import { Roles } from '@/core/types';
import useAuth from '@/shared/context/auth-context';
import { ALL_CUSTOMERS_ID, ALL_SITES_ID } from '@/queries/logs';
import { ExportAllLogsQueryParams } from '@/api/export-logs/types';
import { exportAllLogs } from '@/api/export-logs';
import { downloadFile } from '@/shared/utils/file';
import { AxiosError } from 'axios';
import { ApiError } from '@/shared/types/api-errors';
import useToastMessage from '@/shared/context/toast-message-context';
import dayjs from 'dayjs';
import { getLocationFilterName } from '../locations/utils';

import {
  DataGrid,
  Pager,
  Paging,
  RemoteOperations,
  SearchPanel,
  Toolbar,
  Item,
} from 'devextreme-react/data-grid';
import { CustomStore } from 'devextreme/common/data';
import { getAllAdminLogs } from '@/api/users';
import dxDataGrid, { Column, DataType, InitializedEvent } from 'devextreme/ui/data_grid';

const AVAILABLE_COLUMNS: Array<{
  key: keyof Logs | string;
  label: string;
  render?: (value: Partial<LogsResponse>) => string;
  minWidth?: number | undefined;
  sortable?: boolean;
  searchable?: boolean;
  type?: DataType;
}> = [
  {
    key: 'runDate',
    label: 'Run Date',
    render: (value: Partial<LogsResponse>) =>
      value.runDate ? new Date(value.runDate).toLocaleString() : '',
    minWidth: 180,
    sortable: true,
    searchable: false,
    type: 'datetime'
  },
  { key: 'customerName', label: 'Customer Name', minWidth: 220, sortable: true, searchable: true },
  { key: 'siteName', label: 'Site Name', minWidth: 220, sortable: true, searchable: true },
  { key: 'status', label: 'Status', sortable: true, searchable: true },
  { key: 'deviceType', label: 'Device Type', minWidth: 120, sortable: true, searchable: true },
  {
    key: 'deviceSerialNumber',
    label: 'Device SN',
    minWidth: 180,
    sortable: true,
    searchable: true,
  },
  { key: 'deviceName', label: 'Device Name', minWidth: 150, sortable: true, searchable: true },
  { key: 'userName', label: 'User Name', minWidth: 180, sortable: true, searchable: true },
  { key: 'cycleType', label: 'Cycle Type', minWidth: 110, sortable: true, searchable: true },
  { key: 'cycletime', label: 'Cycle Length', minWidth: 120, sortable: true, searchable: true },
  { key: 'armHeight', label: 'Arm Height', minWidth: 110, sortable: true, searchable: true },
  {
    key: 'batteryCharge',
    label: 'Battery Charge Level',
    minWidth: 220,
    sortable: true,
    searchable: true,
  },
  { key: 'isCharging', label: 'Plugged', sortable: true, searchable: true },
  { key: 'software', label: 'Software', sortable: true, searchable: true },
  { key: 'firmware', label: 'Firmware', sortable: true, searchable: true },
  { key: 'current1_t1', label: 'Current 1 T1', minWidth: 120, sortable: true, searchable: true },
  { key: 'current2_t1', label: 'Current 2 T1', minWidth: 120, sortable: true, searchable: true },
  { key: 'current3_t1', label: 'Current 3 T1', minWidth: 120, sortable: true, searchable: true },
  { key: 'current4_t1', label: 'Current 4 T1', minWidth: 120, sortable: true, searchable: true },
  { key: 'faultEs', label: 'Fault ES', minWidth: 100, sortable: true, searchable: true },
  { key: 'faultMs', label: 'Fault MS', minWidth: 100, sortable: true, searchable: true },
  { key: 'faultCp', label: 'Fault CP', minWidth: 100, sortable: true, searchable: true },
  { key: 'faultWd', label: 'Fault WD', minWidth: 100, sortable: true, searchable: true },
];

const DEFAULT_COLUMN_KEYS = [
  'runDate',
  'customerName',
  'siteName',
  'status',
  'deviceType',
  'deviceSerialNumber',
  'userName',
  'cycleType',
  'cycletime',
];

const STORAGE_KEY = 'leviant-admin-logs-columns';

const LeviantAdminLogsPage: FunctionComponent = (): ReactElement => {
  const { user } = useAuth();
  const { role } = user || {};
  const isAdmin = role === Roles.LEVIANT_ADMIN;

  const [availableColumns, setAvailableColumns] = useState(AVAILABLE_COLUMNS);
  const [dateFilter, setDateFilter] = useState<DateFilter>(DateFilter.LAST_7_DAYS);
  const [customRangeFilter, setCustomRangeFilter] = useState<CustomDateRange>(() => {
    const lastWeekStart = dayjs().subtract(1, 'week').startOf('week');
    const lastWeekEnd = dayjs().subtract(1, 'week').endOf('week');
    return {
      start: lastWeekStart,
      end: lastWeekEnd,
    };
  });
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(ALL_SITES_ID);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [locationsFilter, setLocationsFilter] = useState<SelectedLocation[]>([]);

  const [dataGrid, setDxDataGrid] = useState<dxDataGrid | undefined>(undefined);

  const handleDataGridInitialized = useCallback((e: InitializedEvent) => {
    setDxDataGrid(e.component);
  }, []);

  const dateRange: QueryDateRange = useMemo(
    () => convertDateFilter(dateFilter, customRangeFilter),
    [dateFilter, customRangeFilter]
  );

  const { data: customerSites } = useCustomerSites();

  const [locationsFilterDialog, setLocationsFilterDialog] = useState(false);

  const locationId = useMemo(() => {
    const firstUnfilledIndex = locationsFilter?.findIndex(
      (selected) => selected?.location === undefined
    );
    if (firstUnfilledIndex === 0) return undefined;
    const lastFilledIndex =
      firstUnfilledIndex === -1 ? locationsFilter.length - 1 : firstUnfilledIndex - 1;
    return locationsFilter?.[lastFilledIndex]?.location?.id;
  }, [locationsFilter]);

  const { query: useMetaLogsQuery } = useMetaDataLogs({
    queryParams: {
      siteId: selectedSiteId === ALL_SITES_ID ? undefined : selectedSiteId,
      customerId: customerId === ALL_CUSTOMERS_ID ? undefined : customerId,
      fromDate: dateRange.start || '',
      toDate: dateRange.end || '',
      locationId,
    },
  });

  const { data: logsMetaData } = useMetaLogsQuery;

  const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as string[]) : null;
      return parsed && parsed.length ? parsed : DEFAULT_COLUMN_KEYS;
    } catch {
      return DEFAULT_COLUMN_KEYS;
    }
  });

  useEffect(() => {
    if (logsMetaData?.levels && Array.isArray(logsMetaData.levels) && logsMetaData.levels.length) {
      setAvailableColumns((prev) => {
        const firstDynKey = `location1`;
        if (prev.some(c => String(c.key) === firstDynKey)) return prev;

        const levelColumns = logsMetaData.levels.map((level, index) => ({
          key: `location${index + 1}`,
          label: `${level}`,
          minWidth: 180,
          sortable: false,
          searchable: false,
        }));

        return [...prev, ...levelColumns];
      });

      setSelectedColumnKeys((prev) => {
        const hasDyn = prev.some(k => k.startsWith('location'));
        if (hasDyn) return prev;

        const levelKeys = logsMetaData.levels.map((_, index) => `location${index + 1}`);
        const next = [...prev, ...levelKeys];
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {/* noop */}
        return next;
      });
    }
  }, [logsMetaData?.levels]);

  const [columnsAnchorEl, setColumnsAnchorEl] = useState<HTMLElement | null>(null);
  const columnsOpen = Boolean(columnsAnchorEl);

  const handleOpenColumns = (e: React.MouseEvent<HTMLElement>) =>
    setColumnsAnchorEl(e.currentTarget);
  const handleCloseColumns = () => setColumnsAnchorEl(null);

  const toggleColumn = (key: string) => {
    setSelectedColumnKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // do nothing
      }
      return next;
    });
  };

  const selectAll = () => {
    const allKeys = availableColumns.map((c) => String(c.key));
    setSelectedColumnKeys(allKeys);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allKeys));
    } catch {
      // do nothing
    }
  };

  const clearAll = () => {
    setSelectedColumnKeys([]);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    } catch {
      // do nothing
    }
  };

  const tableColumns = useMemo(
    () =>
      availableColumns
        .filter((c) => selectedColumnKeys.includes(String(c.key)))
        .map((c) => ({
          name: c.label,
          accessors: [String(c.key)] as (keyof LogsResponse)[],
          render: c.render,
          minWidth: c.minWidth,
          sortable: !!c.sortable,
          searchable: !!c.searchable,
          type: c.type,
        })),
    [selectedColumnKeys, availableColumns]
  );

  const selectedLocationTitle = useMemo(() => {
    const selectedLocations = locationsFilter.filter((l) => l.location);
    const locationNames = selectedLocations
      .map((l) => l.location?.name)
      .filter(Boolean) as string[];

    let parts: string[] = [];

    const customerById = customerSites?.find((c) => c.id === customerId);

    let customerFromSite = undefined as typeof customerById | undefined;
    let siteName: string | undefined;
    if (selectedSiteId && selectedSiteId !== ALL_SITES_ID && customerSites?.length) {
      customerFromSite = customerSites.find((c) => c?.sites?.some((s) => s?.id === selectedSiteId));
      const site = customerFromSite?.sites?.find((s) => s?.id === selectedSiteId);
      siteName = site?.name;
    }

    const customer = customerById || customerFromSite;

    if (customer?.name) parts.push(customer.name);
    if (siteName) parts.push(siteName);

    if (!parts.length) {
      parts.push('All Clients');
    }

    if (locationNames.length) {
      parts = [...parts, ...locationNames];
    }

    return parts.join(' / ');
  }, [locationsFilter, selectedSiteId, customerSites, customerId]);

  const { dispatch } = useToastMessage();

  const { mutate: onExportAllLogs } = useMutation({
    mutationFn: (queryParams: ExportAllLogsQueryParams) => exportAllLogs(queryParams),
    onSuccess: (data: string, queryParams) => {
      const dateRange = {
        start: queryParams.fromDate,
        end: queryParams.toDate,
      };

      downloadFile(data, getStatisticsExportFileName('All', dateRange, `logs`));
    },
    onError: (error: AxiosError<ApiError>) => {
      const { statusCode, message } = error.response?.data || {};
      if (statusCode === 400) {
        dispatch({ type: 'error', content: message });
      }
    },
  });

  const buttonTitle = useMemo(
    () => getLocationFilterName(customerId, selectedSiteId, locationsFilter, customerSites),
    [customerId, selectedSiteId, locationsFilter, customerSites]
  );

  const dashboardHeaderControls = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{ width: '100%' }}
    >
      <ExportButton
        label="Export Report"
        type="secondary"
        onClick={() => {
          const visibleColumnFields = tableColumns.map((col) => String(col.accessors[0]));
          onExportAllLogs({
            siteId: selectedSiteId === ALL_SITES_ID ? undefined : selectedSiteId,
            customerId: customerId === ALL_CUSTOMERS_ID ? undefined : customerId,
            locationId,
            fromDate: dateRange.start || '',
            toDate: dateRange.end || '',
            columns: visibleColumnFields,
          });
        }}
      />
      <Stack direction="row" spacing={2}>
        <SelectInput
          label="Date range"
          size="small"
          options={DATE_FILTER_OPTIONS}
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value as DateFilter)}
          sx={{ width: '220px' }}
        />
        {dateFilter === DateFilter.CUSTOM_RANGE && (
          <DateRangePicker
            value={customRangeFilter}
            onChange={setCustomRangeFilter}
            variant="outlined"
            size="small"
          />
        )}
        <Button variant="outlined" onClick={() => setLocationsFilterDialog(true)}>
          {buttonTitle}
        </Button>
      </Stack>
    </Stack>
  );

  const isNotEmpty = (value: string) => value !== undefined && value !== null && value !== '';

  const filtersRef = useRef({
    fromDate: dateRange.start || '',
    toDate: dateRange.end || '',
    siteId: selectedSiteId === ALL_SITES_ID ? undefined : selectedSiteId,
    customerId: customerId === ALL_CUSTOMERS_ID ? undefined : customerId,
    locationId,
  });

  useEffect(() => {
    filtersRef.current = {
      fromDate: dateRange.start || '',
      toDate: dateRange.end || '',
      siteId: selectedSiteId === ALL_SITES_ID ? undefined : selectedSiteId,
      customerId: customerId === ALL_CUSTOMERS_ID ? undefined : customerId,
      locationId,
    };
    dataGrid?.getDataSource()?.reload();
  }, [dateRange, selectedSiteId, customerId, locationId, dataGrid]);

  const customDataSourceRef = useRef<CustomStore | null>(null);
  if (!customDataSourceRef.current) {
    customDataSourceRef.current = new CustomStore({
      key: 'id',
      load: (loadOptions: any) => {
        const params: any = { ...filtersRef.current };

        [
          'filter',
          'searchExpr',
          'searchOperation',
          'searchValue',
          'sort',
          'skip',
          'take',
        ].forEach((i) => {
          if (i in loadOptions && isNotEmpty(loadOptions[i])) {
            params[i] = JSON.stringify(loadOptions[i]);
          }
        });

        return getAllAdminLogs(params)
          .then((response) => ({
            data: response.data,
            totalCount: response.totalItems,
          }))
          .catch(() => {
            throw 'Network error';
          });
      },
    });
  }
  const customDataSource = customDataSourceRef.current!;

  // Stable columns for DataGrid (default sort by runDate desc)
  const dxColumns = useMemo(() => {
    return tableColumns.map<Column>((col) => {
      const isRunDate = (col.accessors[0] as string) === 'runDate';
      return {
        dataField: col.accessors[0] as string,
        caption: col.name,
        allowFiltering: col.searchable,
        allowSearching: col.searchable,
        allowSorting: col.sortable,
        dataType: col.type,
        sortOrder: isRunDate ? 'desc' : undefined,
        sortIndex: isRunDate ? 0 : undefined,
        cellRender: (e: any) => {
          if (col.render) {
            return col.render(e.data);
          }
          const value = e.data[col.accessors[0]];
          return value !== null && value !== undefined ? String(value) : '';
        },
        minWidth: col.minWidth,
      };
    });
  }, [tableColumns]);

  const allowedPageSizes = useMemo(() => [10, 25, 50, 100], []);
  const scrollingOptions = useMemo(() => ({ showScrollbar: 'always' as const }), []);
  const panelWidth = 360;

  return (
    <AdminPanelPage title="Logs" headerControls={dashboardHeaderControls}>
      <Box component={Paper} p={3} sx={{ position: 'relative' }}>
        <LocationFiltersDialog
          open={locationsFilterDialog}
          onClose={() => setLocationsFilterDialog(false)}
          customerSites={customerSites}
          isAdmin={isAdmin}
          applyLocationFilters={(selectedLocations) => {
            setLocationsFilter(selectedLocations);
          }}
          onSiteChange={setSelectedSiteId}
          onCustomerChange={setCustomerId}
        />
        <Stack direction="column" spacing={3}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box>{selectedLocationTitle}</Box>
            <Popover
              open={columnsOpen}
              anchorEl={columnsAnchorEl}
              onClose={handleCloseColumns}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box p={2} sx={{ maxHeight: 420, overflowY: 'auto', minWidth: 260, maxWidth: 1200 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Columns
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button size="small" onClick={selectAll}>
                    Select all
                  </Button>
                  <Button size="small" onClick={clearAll}>
                    Clear
                  </Button>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridTemplateRows: 'repeat(6, auto)',
                    gap: 1,
                  }}
                >
                  {availableColumns.map((col) => {
                    const key = String(col.key);
                    return (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={selectedColumnKeys.includes(key)}
                            onChange={() => toggleColumn(key)}
                            size="small"
                          />
                        }
                        label={col.label}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Popover>
          </Stack>

          <DataGrid
            className="dx-static-scroll"
            width="100%"
            height="100%"
            columnAutoWidth={false}
            scrolling={scrollingOptions}
            onInitialized={handleDataGridInitialized}
            columns={dxColumns}
            dataSource={customDataSource}
          >
            <Toolbar>
              <Item location="after">
                <Button variant="contained" onClick={handleOpenColumns} sx={{ mr: '8px' }}>
                  Columns
                </Button>
              </Item>
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <SearchPanel visible={true} width={panelWidth} />
            <RemoteOperations paging sorting filtering />
            <Paging defaultPageSize={50} />
            <Pager showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} />
          </DataGrid>
        </Stack>
      </Box>
    </AdminPanelPage>
  );
};

export default LeviantAdminLogsPage;
